import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Command, Mail } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password required"); return; }
    if (mode === "register" && !name) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(email, password, name, inviteCode, "");
        toast.success("Account created!");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Something went wrong";
      if (msg.includes("owner has been notified")) {
        setRequestSent(true);
        toast.success("Request sent to team owner");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show success message after request is sent
  if (requestSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 noise-bg px-4" data-testid="login-page">
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Request Sent!
          </h1>
          <p className="text-sm text-stone-500 mb-6">
            The team owner has been notified of your registration request. They'll send you an invite code if approved.
          </p>
          <Button
            onClick={() => { setRequestSent(false); setMode("login"); }}
            variant="outline"
            className="w-full"
          >
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 noise-bg px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-stone-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Command className="w-7 h-7 text-stone-50" />
          </div>
          <h1 className="text-3xl font-semibold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            Command Center
          </h1>
          <p className="text-sm text-stone-500">Your business management platform</p>
        </div>

        <div className="bg-white border border-stone-200/60 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-6">
          <div className="flex gap-1 bg-stone-100 p-1 rounded-lg mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === "login" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            >
              Sign In
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setMode("register")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === "register" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium text-stone-600">Name</Label>
                <Input
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-stone-600">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-stone-600">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="mt-1"
              />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium text-stone-600">Invite Code</Label>
                <Input
                  data-testid="register-invite-code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Enter invite code"
                  className="mt-1"
                />
                <p className="text-xs text-stone-400 mt-1">Don't have a code? Submit to request access.</p>
              </div>
            )}
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-[0.98] transition-all font-medium h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {mode === "login" ? "Sign In" : (inviteCode ? "Create Account" : "Request Access")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
